import VueCookies from 'vue-cookies';
import { createStore } from "vuex";
import { COOKIE_KEYS } from "@/utils/keys";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

const SIMULATED_SUPPLIER_COOKIE_TTL = "365d";
const isNativePreferencesAvailable = () => Capacitor.isNativePlatform();

const setPreference = (key, value) => {
  if (!isNativePreferencesAvailable() || value === undefined || value === null) return;
  Preferences.set({ key, value: String(value) });
};

const getPreference = async (key) => {
  if (!isNativePreferencesAvailable()) return null;
  const result = await Preferences.get({ key });
  return result.value || null;
};

const getPersistedValue = async (key) => {
  const cookieValue = VueCookies.get(key);
  if (cookieValue) return cookieValue;

  const preferenceValue = await getPreference(key);
  if (preferenceValue) {
    VueCookies.set(key, preferenceValue, SIMULATED_SUPPLIER_COOKIE_TTL);
  }
  return preferenceValue;
};

const persistValue = (key, value) => {
  const persistedValue = value === undefined || value === null ? "" : value;
  VueCookies.set(key, persistedValue, SIMULATED_SUPPLIER_COOKIE_TTL);
  setPreference(key, persistedValue);
};

export default createStore({
  state: {
    simulatedSupplierId: null,
    simulatedSupplierName: null,
    simulatedSupplierLogoName: null,
    simulatedSupplierTaxNumber: null,
    catalogEnabled: false,
    stockEnabled: false,
    purchaseEnabled: false,
    expenseEnabled: false,
    employeeEnabled: false,
    notificationEnabled: false,
    reportEnabled: false
  },

  mutations: {
    SET_SIMULATED_SUPPLIER_ID(state, id) { state.simulatedSupplierId = id; },
    SET_SIMULATED_SUPPLIER_NAME(state, name) { state.simulatedSupplierName = name; },
    SET_SIMULATED_SUPPLIER_LOGO_NAME(state, logo) { state.simulatedSupplierLogoName = logo; },
    SET_SIMULATED_SUPPLIER_TAX_NUMBER(state, taxNumber) { state.simulatedSupplierTaxNumber = taxNumber; },
    SET_CATALOG_ENABLED(state, enabled) { state.catalogEnabled = Boolean(enabled); },
    SET_STOCK_ENABLED(state, enabled) { state.stockEnabled = Boolean(enabled); },
    SET_PURCHASE_ENABLED(state, enabled) { state.purchaseEnabled = Boolean(enabled); },
    SET_EXPENSE_ENABLED(state, enabled) { state.expenseEnabled = Boolean(enabled); },
    SET_EMPLOYEE_ENABLED(state, enabled) { state.employeeEnabled = Boolean(enabled); },
    SET_NOTIFICATION_ENABLED(state, enabled) { state.notificationEnabled = Boolean(enabled); },
    SET_REPORT_ENABLED(state, enabled) { state.reportEnabled = Boolean(enabled); }
  },

  actions: {
    setSimulatedSupplier({ commit }, supplier) {
      commit("SET_SIMULATED_SUPPLIER_ID", supplier.id);
      commit("SET_SIMULATED_SUPPLIER_NAME", supplier.companyName);
      commit("SET_SIMULATED_SUPPLIER_LOGO_NAME", supplier.logoName);
      commit("SET_SIMULATED_SUPPLIER_TAX_NUMBER", supplier.taxNumber);

      persistValue(COOKIE_KEYS.SIMULATED_SUPPLIER_ID, supplier.id);
      persistValue(COOKIE_KEYS.SIMULATED_SUPPLIER_NAME, supplier.companyName);
      persistValue(COOKIE_KEYS.SIMULATED_SUPPLIER_LOGO, supplier.logoName);
      persistValue(COOKIE_KEYS.SIMULATED_SUPPLIER_TAX, supplier.taxNumber);
    },

    async loadSimulatedSupplierFromCookies({ commit }) {
      const id = await getPersistedValue(COOKIE_KEYS.SIMULATED_SUPPLIER_ID);
      const name = await getPersistedValue(COOKIE_KEYS.SIMULATED_SUPPLIER_NAME);
      const logo = await getPersistedValue(COOKIE_KEYS.SIMULATED_SUPPLIER_LOGO);
      const tax = await getPersistedValue(COOKIE_KEYS.SIMULATED_SUPPLIER_TAX);

      if (id) commit("SET_SIMULATED_SUPPLIER_ID", id);
      if (name) commit("SET_SIMULATED_SUPPLIER_NAME", name);
      if (logo) commit("SET_SIMULATED_SUPPLIER_LOGO_NAME", logo);
      if (tax) commit("SET_SIMULATED_SUPPLIER_TAX_NUMBER", tax);
    }
  },

  getters: {
    simulatedSupplier: (state) => ({
      id: state.simulatedSupplierId,
      name: state.simulatedSupplierName,
      logo: state.simulatedSupplierLogoName,
      taxNumber: state.simulatedSupplierTaxNumber
    }),
    catalogEnabled: (state) => state.catalogEnabled,
    stockEnabled: (state) => state.stockEnabled,
    purchaseEnabled: (state) => state.purchaseEnabled,
    expenseEnabled: (state) => state.expenseEnabled,
    employeeEnabled: (state) => state.employeeEnabled,
    notificationEnabled: (state) => state.notificationEnabled,
    reportEnabled: (state) => state.reportEnabled
  }
});
